import { Color3, Color4, PhysicsMotionType, PhysicsShapeType, Vector2, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { DecorationConfig } from '../DecorationGenerator';
import { EnemyConfig } from '../EnemyGenerator';
import { EnemyPrefabs } from '../EnemyPrefabs';
import { FogConfig } from '../FogConfig';
import { Level } from '../Level';
import {
  CameraConfig,
  CollectibleDistributionConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PlayerConfig,
  PostProcessConfig,
  SoundConfig,
  WaterConfig,
  WayGenerationConfig,
} from '../LevelConfig';
import { LightConfig } from '../LightConfig';
import { HeightMapConfig, SkyBoxConfig } from '../MeshFactory';
import { ObstacleConfig } from '../ObstacleGenerator';
import { ObstaclePrefabs } from '../ObstaclePrefabs';
import { PathFactory } from '../PathFactory';
import { TextureAtlas } from '../TextureAtlas';
import { LevelTemplate } from './LevelTemplate';

export class TemplateWayVertical implements LevelTemplate {
  create(): LevelConfig {
    const probabilityMin = 0.05;
    const probabilityMid = 0.1;
    const probabilityMax = 0.15;

    const skyboxConfig: SkyBoxConfig = {
      url: 'assets/skybox/skybox_16.jpg',
      rotationAxis: 'y',
      rotationSpeed: 0.00001,
    };
    const fogConfig = new FogConfig();
    const lightConfig = new LightConfig();

    const postProcessConfig: PostProcessConfig = {
      fxaa: false,
      motionBlur: 0,
      //blur: { kernel: 32, strength: 1, x: 1, y: 0 },
      highlights: false,
      sepia: false,
    };

    const materialConfig: MaterialConfig = {
      colorMaterialBaseColor: new Color3(0.5, 0.7, 0.9),
      colorMaterialCount: 0,
      textures: [
        { url: 'assets/textures/road_chappy.jpg', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground2.avif', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground.avif', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/green-grass.jpg', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/stone-ground.jpg', uScale: 2, vScale: 1 },
        { url: 'assets/textures/stone-ground2.jpg', uScale: 8, vScale: 0.5 },
        { url: 'assets/textures/stone-ground3.jpg', uScale: 2, vScale: 1 },
        { url: 'assets/textures/stone-ground4.jpg', uScale: 2, vScale: 1 },
        { name: 'way', color: '#a5a3f9', alpha: 0.2, metallic: 1, roughness: 0.05 },
        //{ name: 'way2', shader: 'voronoi', alpha: 0.1 },
        { url: 'assets/textures/stone-ground-colored.jpg', uScale: 3, vScale: 3 },
        { url: 'assets/textures/wood-ground.jpg', uScale: 2, vScale: 0.3 },
      ],
      globalTextureNames: [],
      materials: [],
    };

    const wayConfig: WayGenerationConfig = {
      width: 16,
      height: 0.3,
      shape: PathFactory.shapeHalfpipe(8, 4, 0, 1),
      segmentsAcross: 12,
      yOffset: -1,
      material: 'stone-ground2',
      gapProbability: 0,
      gapSize: { min: 1, max: 2 },
      holeProbability: 0.3, // % chance per path point
      holeSize: { min: 4, max: 8 },
      holeAngle: { min: 0, max: Math.PI * 2 }, // full rotation (all sides)
    };

    const pathConfig: PathConfig = {
      start: new Vector3(0, 0, 0),
      destination: new Vector3(0, 100, 100),
      pathPoints: 10,
      segmentsPerCurve: 15,
      pathCurvature: 1.5,
      pathHilliness: 0.5,
      pathScale: 3.0,
      pathMinCurveAngle: 90,
      laneWidth: 3,
      spaceAround: 50,
      //pathBoundary: 10,
      predefined: PathFactory.spiralPath(
        new Vector3(0, 0, 0), // start
        new Vector3(0, 100, 0), // destination
        80, // startRadius
        50, // destinationRadius (narrower)
        2, // full turns
        8, // control point count
        8 // curve points count
      ),
    };

    const soundConfig: SoundConfig = {
      backgroundMusic: 'sunnySkies',
      backgroundMusicVolume: 1.0,
    };

    const cameraConfig: CameraConfig = {
      alignToPath: false,
      distance: 8,
      heightOffset: 1.2,
      smoothing: 40,
      alphaSmoothing: 1,
      collision: false,
      orthographic: false,
      orthographicZoom: 8,
      orthographicAlpha: Math.PI / 10,
      orthographicBeta: Math.PI / 3,
    };

    const collectibleConfig: CollectibleDistributionConfig = {
      offset: new Vector3(0, 2, 0),
      probability: 0.1,
      minPoint: 10,
      mixPatternTypes: false,
      maxPatternSize: 3,
      spawnRange: 60,
      despawnRange: 90,
    };

    const waterConfig: WaterConfig = {
      level: -30,
      depth: 50,
      size: 2000,
      effect: true,
      mesh: true,
      renderTargetSize: new Vector2(512, 512),
      bumpTextureUrl: 'assets/textures/waterbump2.png',
      bumpScale: 10,
      windForce: 1,
      waveHeight: 0.1,
      waveLength: 0.1,
      bumpHeight: 0.1,
      windDirection: new Vector2(1, 1),
      waterColor: new Color3(0, 0, 221 / 255),
      colorBlendFactor: 0.2,
      alpha: 0.75,
    };

    const decorationConfig: DecorationConfig = {
      assets: [
        new Asset('treeA', AssetType.MESH, 'assets/model/nature/', 'tree_cone.glb'),
        new Asset('treeB', AssetType.MESH, 'assets/model/nature/', 'tree_default.glb'),
        new Asset('treeC', AssetType.MESH, 'assets/model/nature/', 'tree_palm.glb'),
        new Asset('rockA', AssetType.MESH, 'assets/model/survival/', 'rock-a.glb'),
        new Asset('rockB', AssetType.MESH, 'assets/model/nature/', 'rock_tallB.glb'),
        new Asset('stones', AssetType.MESH, 'assets/model/nature/', 'path_stone.glb'),
        new Asset('log', AssetType.MESH, 'assets/model/nature/', 'log.glb'),
        new Asset('rail', AssetType.MESH, 'assets/model/racing/', 'railDouble.glb'),
        new Asset('dirt', AssetType.MESH, 'assets/model/nature/', 'crops_dirtRow.glb'),
        new Asset('grass', AssetType.MESH, 'assets/model/nature/', 'grass_large.glb'),
        new Asset('flowerYellow', AssetType.MESH, 'assets/model/nature/', 'flower_yellowA.glb'),
        new Asset('flowerRed', AssetType.MESH, 'assets/model/nature/', 'flower_redB.glb'),
        new Asset('flowerPurple', AssetType.MESH, 'assets/model/nature/', 'flower_purpleC.glb'),
        new Asset('cloud', AssetType.MESH, 'assets/model/platformer_cute/', 'cloud.glb'),
      ],
      assetObjects: [
        new AssetObject('treeA').withType('nature'),
        new AssetObject('treeB').withType('nature'),
        new AssetObject('treeC').withType('nature'),
        new AssetObject('rail').withType('rail'),
        new AssetObject('rockA').withType('rockA'),
        new AssetObject('rockB').withType('rockB'),
        new AssetObject('dirt').withType('dirt'),
        new AssetObject('flowerYellow').withType('flowers'),
        new AssetObject('flowerRed').withType('flowers'),
        new AssetObject('flowerPurple').withType('flowers'),
        new AssetObject('grass').withType('nature'),
        new AssetObject('log').withType('nature'),
        new AssetObject('stones').withType('stones'),
        new AssetObject('cloud').withType('cloud'),
      ],
      distributions: [
        // {
        //   types: ['buildingJ'],
        //   mode: 'absolute',
        //   physics: true,
        //   absolutePlacements: [
        //     {
        //       position: new Vector3(-5.458, 5.693, 68.117),
        //       rotation: new Vector3(0.0, 0.0, -0.742),
        //       scale: new Vector3(1.473, 1.473, 1.473),
        //     },
        //   ],
        // },
        {
          types: ['flowers'],
          mode: 'mesh',
          meshSurface: 'edges',
          spread: 0.1,
          density: 30,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipProbability: 0,
          skipOccupied: false,
          rolling: false,
          minPoint: 2,
        },

        {
          types: ['cloud'],
          mode: 'zone',
          zone: { min: -500, max: 500 },
          density: 2,
          interval: 2,
          alpha: 0.5,
          offset: new Vector3(0, 40, 0),
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 4, max: 12 },
          skipProbability: 0,
          skipOccupied: false,
        },

        {
          types: ['stones'],
          mode: 'fixed',
          alignToPath: false,
          offset: new Vector3(0, 0.1, 0),
          interval: 4,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.5, max: 1.5 },
          skipProbability: 0.2,
          minPoint: 2,
        },

        {
          types: ['rockA', 'rockB'],
          mode: 'zone',
          zone: { min: -70, max: -170 },
          density: 5,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipProbability: 0.2,
          alignToFace: true,
          useLevelYPosition: true,
        },
      ],
    };

    const minPoint = 10;
    const obstacleConfig: ObstacleConfig = ObstaclePrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: wayConfig.width,
      spawnRange: 60,
      despawnRange: 90,
      obstacleColor: 'yellow',
    });

    const enemyConfig: EnemyConfig = EnemyPrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: wayConfig.width,
      spawnRange: 60,
      despawnRange: 90,
    });

    // grounds and ceilings matrices should match on their edges, ensure this
    const groundWidth = 1500;
    const groundHeight = 1500;
    const groundSubdivisions = 100;

    const heightMapConfig = HeightMapConfig.builder()
      .name('heightmap-ground')
      .heightMapImageUrl('assets/heightmap/snowdon.png')
      //.heightMapImageUrl('assets/heightmap/ambientocclusion.jpg')
      .width(groundWidth)
      .height(groundHeight)
      .updatable(true)
      .subdivisions(groundSubdivisions)
      .castShadows(false)
      .maxHeight(70)
      .minHeight(0)
      .metadata({
        centerOnPath: false,
      })
      .pathWidth(50) // Width of the corridor
      .pathTransitionWidth(7) // Steepness of the walls (greater = gentler)
      .pathYOffset(4) // Optional: Y-offset
      .terrainTextureAtlas('tiles2')
      .terrainTextureName1('dirt')
      .terrainTextureName2('ground')
      .terrainTextureName3('lava')
      .terrainTextureScale(new Vector3(50, 50, 50))
      .backFaceCulling(false)
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, -55, 0))
      .build();

    const playerConfig: PlayerConfig = {
      startPathIndex: 0,
      startOffset: new Vector3(-2, 5, 8),
      autoRun: 0,
      speed: 10,
      jumpSpeed: 11,
      backwardSpeed: 4,
      turnSpeed: 10,
      dashSpeed: 12,
      projectileSpeed: 20,
    };

    const atlasConfig = [
      new TextureAtlas('tiles2', 'assets/atlas/', 'tiles2.png', 'tiles2.json'),
      new TextureAtlas('cartoon1', 'assets/atlas/', 'cartoon1.png', 'cartoon1.json'),
    ];

    const spriteConfig = [
      {
        asset: new Asset('palm_cartoon', AssetType.IMAGE, 'assets/sprites/', 'palm_cartoon.png'),
        scale: { min: 2, max: 7 },
        capacity: 400,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1088,
        cellHeight: 1200,
        name: 'palm_cartoon',
        offsetY: -1,
        yRange: { min: -5, max: 300 },
      },
      {
        asset: new Asset('tree_cartoon', AssetType.IMAGE, 'assets/sprites/', 'tree_cartoon.png'),
        scale: { min: 2, max: 7 },
        capacity: 400,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1136,
        cellHeight: 1200,
        name: 'tree_cartoon',
        offsetY: -1,
        yRange: { min: -5, max: 300 },
      },
    ];

    const config = new LevelConfig(
      'Test Level',
      2, // seed
      100, // timelimit
      Level.fullGravity, // gravity
      waterConfig.level, // killing height
      100, // height above ground
      40, // checkpoint interval
      1, // goal end offset
      playerConfig,
      cameraConfig,
      pathConfig,
      lightConfig,
      skyboxConfig,
      materialConfig
    );

    config.sound = soundConfig;
    config.fog = fogConfig;
    config.way = wayConfig;
    config.collectible = collectibleConfig;
    config.postProcess = postProcessConfig;
    config.heightMap = heightMapConfig;
    //config.decoration = decorationConfig;
    config.enemy = enemyConfig;
    config.obstacle = obstacleConfig;
    config.water = waterConfig;
    config.atlasses = atlasConfig;
    config.sprites = spriteConfig;

    return config;
  }
}
